/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React from "react";
import TeamAvgCharts from "./TeamAvgCharts";
import { Table, Space, Row, Col } from "antd";
import "../../Styles/styles-ResultsTable.css";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import "./style.css";
import { setPlayerFilter } from "../../store/actions/player.actions.js";
import {
  getPrediction,
  purdueFootballMetricOrder,
} from "../../Utils/chart_utils";
import {
  setTeamFilter,
  setTeamPresets,
  setGPSStatDefaults,
} from "../../store/actions/team.actions.js";
import { trainingAPI } from "../../api/TrainingAPI";
import { useSelector } from "react-redux";
import { DATE_FORMAT } from "../../Utils/date_utils";
import { getDropdownKey, fillMissingDatesForChart } from "../../Utils/chart_utils";
import { useMemo, useState, useEffect } from "react";
import { getColorOfValue } from "../../Utils/color_utils";
import { DataColorUtil } from "../../Utils/color_utils";
import { StatTileV2 } from "../StatTileV2/StatTileV2.jsx";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { useDataAccess, DataAccessDenied } from "@components/PermissionedComponents";
import { getPercentages as percentages } from "../../Colors/percentages";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx";
import { audioMessage as message } from "../Common/Notifications/audioMessage";

const todayString = dayjs().format(DATE_FORMAT);


function round(num) {
  return Math.round((num + Number.EPSILON) * 10) / 10;
}

export function TeamAvgTable({ type, dataColorUtil, handleComparisonOptionChange, ...props }) {
  const dispatch = useDispatch();
  const PRESET_TYPE = PresetType.Gps;
  const DEFAULT_PRESET = 'Default'

  const {
    loading: { gps: loading },
    costPerMin,
    practicePlan,
		dropdowns: {gps: gpsDropdowns},
    info: { display_name: teamName },
    filter: { showPredictions, startDate, endDate, showWeekly, showAllDays },
    gpsStatDefaults,
    color_strategies: colorStrategies,
    misc: { comparison_types },
    presets,
  } = useSelector(({ team }) => team);

  const {
    user,
  } = useAuth0();
  const {
    filter: { gpsData, player, comparisonOption },
  } = useSelector((store) => store[type]);

  const [statMetrics, setStatMetrics] = useState([]);
  const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)
  const [unsavedChanges, setUnsavedChanges] = useState(false)

  const { isDenied: isDataDenied } = useDataAccess();

  useMetricDropdowns(['gps']);

  let extraDataObject = useMemo(
    () =>
      [...new Set(practicePlan.map((p) => p.date))].reduce((cur, date) => {
        const obj = props.data.metrics.reduce(
          (c, metric) => {
            c[metric.key] = getPrediction(
              date,
              practicePlan,
              costPerMin,
              metric.key
            );
            return c;
          },
          {
            date,
            RPE: "",
            session_RPE: "",
            total_time: practicePlan
              .filter((p) => p.date === date)
              .reduce((sum, it) => sum + it.duration, 0),
          }
        );
        cur[date] = obj;
        return cur;
      }, {}),
    [practicePlan, props.data, costPerMin]
  );

  const initialColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: {
        compare: (a, b) => a.date.localeCompare(b.date),
        multiple: 1,
      },
    },
    {
      title: "Game",
      dataIndex: "opponent",
      key: "game",
      sorter: {
        compare: (a, b) => a.opponent?.localeCompare(b?.opponent),
        multiple: 1,
      },
    },
    {
      title: "Total Time",
      dataIndex: "total_time",
      key: "total_time",
      sorter: {
        compare: (a, b) => a.total_time - b.total_time,
        multiple: 2,
      },
      render: (total_time, row) => (
        <span>
          {round(total_time || extraDataObject[row.date]?.["total_time"] || row.total_time_pre || 0)}
        </span>
      ),
    },
  ];

  const columns = useMemo(() => {
    return props.data?.metrics?.filter((e) => e.visible === true || e.visible === undefined).reduce((cur, metric) => {
      let metricName = metric.name;
      const key = metric.key;
      if (props.hideDistanceZones) {
        if (metricName === "Distance") {
          metricName = "Total " + metricName;
        }
      }

      if (props.team_name === "Orlando Pride") {
        if (metricName === "Acceleration" || metricName === "Deceleration") {
          metricName += " Z4-Z6";
        }
      }

      return [
        ...cur,
        {
          title: metricName,
          dataIndex: key,
          key,
          sorter: {
            compare: (a, b) => a[key] - b[key],
            multiple: cur.length,
          },
          render: (value, row) => {
            const curr_date_has_training = row.total_time !== undefined;
            const curr_date_has_prediction = row[key + "_pre"] !== undefined && row[key + "_pre"] !== null;

            const prediction = row[key + "_pre"] ?? 0;
            
            if ((!value || value === 0) && curr_date_has_prediction && !curr_date_has_training) {
              value = prediction;
            }

            if (!value && !prediction) {
              return null;
            }

            if (value === "" && (key === "RPE" || key === "session_RPE"))
              return null;
            
            const diff = prediction - +value;
            const percentage = (diff / +value) * 100.0;
            const diffColor =  Math.abs(percentage) <= 5 ? "#28a745" : Math.abs(percentage) <= 15 ? "#ffc107" : "#dc3545";
            const diffComp =
              showPredictions &&
              curr_date_has_prediction &&
              curr_date_has_training &&
              prediction !== 0 &&
              value ? (
                <span style={{ color: diffColor }}>
                  ({percentage === 0 ? "" : value > prediction ? "+" : "-"}
                  {Math.abs(round(percentage))}%)
                </span>
              ) : null;

            let color = "#000000";
            if (row?.colors?.[key]) {
              color = row.colors[key];
            }
            
            return (<>
              <span style={{ color }}>{value}</span> {diffComp}
              </>
            );
          },
        },
      ];
    }, initialColumns);
  }, [
    initialColumns,
    props.data.metrics,
    props.data.colors,
    practicePlan,
    costPerMin,
    showPredictions,
    props.hideDistanceZones,
    dataColorUtil,
  ]);

  const chartData = props.data.data
    .filter((dt) => {
      let start = dayjs(startDate, 'YYYY-MM-DD');
      let dtDate = dayjs(dt.date, 'YYYY-MM-DD');
      let end = dayjs(endDate, 'YYYY-MM-DD');
      
      if (showWeekly) {
        start = start.startOf('week');
      }

      return (
        start <= dtDate && dtDate <= end
      );
    })
    .map((day) =>
      props.data.metrics.reduce(
        (cur, metric) => ({
          ...cur,
          [metric.key + "_pre"]: day[metric.key + "_pre"] ?? 0,
        }),
        day
      )
    );

  const filledChartData = showAllDays && !showWeekly
    ? fillMissingDatesForChart(chartData, startDate, endDate)
    : chartData;

  const handleChangeMetric = (index) => (key) => {
    setStatMetrics((prevStatMetrics) => {
      const newStatMetrics = [...prevStatMetrics];
      newStatMetrics[index] = key;
      return newStatMetrics;
    });

    setUnsavedChanges(true)
  };

  const onChangeStats = () => {
    if (type === "team") {
      dispatch(setTeamFilter({ gpsData: !gpsData }));
    } else if (type === "player") {
      dispatch(setPlayerFilter({ gpsData: !gpsData }));
    }
  };

  const handleSaveMetrics = async () => {
    try {
        if (currentSelectedTilePreset === DEFAULT_PRESET) {
            await trainingAPI.updateDefaultStats(statMetrics, "gps");
            dispatch(setGPSStatDefaults(statMetrics));
        } else {
            const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
            await trainingAPI.updatePreset({
                name: currentSelectedTilePreset, type: PRESET_TYPE, config: statMetrics, preset_id: presetModified.preset_id
            });
            presetModified.config = statMetrics
            dispatch(setTeamPresets(presets))
        }
        message.success("Saved!");
    } catch (e) {
        console.log(e);
        message.error("Error. Unable to edit ");
    }
  };

  if (isDataDenied("gps")) {
    return <DataAccessDenied streams={["gps"]} variant="inline" />;
  }

  return (
    <>
      <div
        className="flex flex-col justify-center items-center w-full"
        id="statistics-widget"
      >
        <Row
          gutter={16}
          style={{ width: "80%", marginBottom: 16, justifyContent: 'center', gap: '10px' }}>
          <PresetSelection 
            presets={presets} 
            user={user} 
            presetType={PRESET_TYPE} 
            statDefaults={gpsStatDefaults} 
            currStatMetrics={statMetrics}
            setStatMetrics={setStatMetrics} 
            currentSelectedTilePreset={currentSelectedTilePreset}
            setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
            comparisonTypes={comparison_types}
            comparisonOption={comparisonOption}
            setComparisonOption={handleComparisonOptionChange}
            handleSave={handleSaveMetrics}
            warnUnsavedChanges={unsavedChanges}
            setWarnUnsavedChages={setUnsavedChanges}
            />
        </Row>

        <Row
          gutter={16}
          style={{ width: "80%", marginBottom: 16 }}
          className="exportable tab-testing testing-stats"
        >
          {statMetrics?.map((metric, i) => {
            return (
              <Col span={6} key={i}>
                <CompactStatTile
                  dropdowns={gpsDropdowns}
                  metric_identifier_with_stat={metric}
                  onChange={handleChangeMetric(i)}
                  option={comparisonOption}
                  endDate={endDate}
                  startDate={startDate}
                  isCompact={true}
                  type={type}
                  comparison={comparisonOption}
                  positionFilter={
                    props.position_filter
                  }
                  />
              </Col>
            );
          })}
        </Row>
      </div>
      {/* <div className="legend-container-team-table">
        <div className="legends-wrapper">
          <span className="legend-icon-green"></span>
          <div> 35% &lt; of Game Baselines</div>
        </div>

        <div className="legends-wrapper">
          <span className="legend-icon-yellow"></span>
          <div> 35-65% of Game Baselines</div>
        </div>

        <div className="legends-wrapper">
          <span className="legend-icon-red"></span>
          <div> &gt;65% of Game Baselines</div>
        </div>
      </div> */}

      <div className="team-table">
        <Space>
          <div className="resultstable-table exportable tab-gpsdata gpsdv-table">
            <Table
              rowClassName={(row, index) => {
                let classname_string = "";
                if (props.showAll) {
                  classname_string += " ant-table-row-no-shadow";
                }
                if (row.date > todayString || row.total_time === undefined) {
                  classname_string += " team-table-prediction-row";
                }
                return classname_string;
              }}
              columns={
                teamName === "Purdue Football"
                  ? purdueFootballMetricOrder(
                      ["date", "game", "total_time"],
                      columns
                    )
                  : columns
              }
              size="small"
              dataSource={props.data["data"].filter(
                (dt) =>
                  {
                    let start = dayjs(startDate, 'YYYY-MM-DD');
                    let dtDate = dayjs(dt.date, 'YYYY-MM-DD');
                    let end = dayjs(endDate, 'YYYY-MM-DD');
                    let today = dayjs(todayString, 'YYYY-MM-DD');
                    
                    if (showWeekly) {
                      start = start.startOf('week');
                    }

                    return (
                      start <= dtDate &&
                      dtDate <= end &&
                      (showPredictions || dtDate <= today)
                    );
              
                  }).sort((a, b) => {
                return dayjs(a.date, 'YYYY-MM-DD') - dayjs(b.date, 'YYYY-MM-DD')
              })}
              pagination={
                props.showAll
                  ? false
                  : {
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "30"],
                      position: ["topLeft"],
                    }
              }
              loading={loading}
            />
          </div>
        </Space>
      </div>

      <div className="resultstable-graphs">
        {props.data.length !== 0 ? (
          <TeamAvgCharts
            data_input={{ ...props.data, data: filledChartData }}
            showPredictions={showPredictions}
            showTravel={props.showTravel}
            showACWR={props.showACWR}
            showRest={props.showRest}
            player_data={props.player_name}
            hideDistanceZones={props.hideDistanceZones}
            team_name={teamName}
            dataColorUtil={dataColorUtil}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
}
